// ===== Global theme (light / dark) =====
// Wrap your app once with <ThemeProvider> and read the theme anywhere with
// useTheme(). The choice persists to localStorage and falls back to the OS
// preference on first run.
//
//   // App.js
//   import { ThemeProvider } from "@/theme/ThemeProvider";
//   <ThemeProvider><App /></ThemeProvider>
//
//   // anywhere
//   const { theme, setTheme, toggleTheme } = useTheme();
//
//   // a Settings row
//   import { ThemeToggle } from "@/theme/ThemeProvider";
//   <ThemeToggle />

import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "fretless-theme";

const ThemeContext = createContext({
  theme: "dark",
  setTheme: () => {},
  toggleTheme: () => {},
});

function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (e) { /* ignore */ }
  // First run: honour the OS preference (raw-hex audit is complete, so
  // light-OS visitors get a fully-themed light page). Users can still
  // override via the Dashboard → Customize → Appearance toggle, and that
  // choice persists in localStorage above. Defaults to dark otherwise.
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch (e) { /* ignore */ }
    // Expose to CSS / non-React surfaces: html[data-theme="light"]
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = theme;
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// ---- Drop-in Settings control -------------------------------------------
// A small segmented Light / Dark switch. Styling is self-contained so it
// works regardless of where you place it.
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { id: "light", label: "Light" },
    { id: "dark", label: "Dark" },
  ];
  const isLight = theme === "light";
  const track = isLight ? "#EFECE3" : "#1f1f22";
  const border = isLight ? "#E4E0D4" : "#262629";
  const activeBg = isLight ? "#FCFBF8" : "#2a2a2e";
  const activeText = isLight ? "#262420" : "#f5f3ee";
  const idleText = isLight ? "#9C978A" : "#6b6963";

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      style={{
        display: "inline-flex",
        gap: "2px",
        backgroundColor: track,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        padding: "3px",
      }}
    >
      {opts.map((o) => {
        const active = theme === o.id;
        return (
          <button
            key={o.id}
            role="radio"
            aria-checked={active}
            onClick={() => setTheme(o.id)}
            style={{
              padding: "7px 16px",
              borderRadius: "7px",
              border: "none",
              cursor: "pointer",
              fontFamily: "Geist, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              backgroundColor: active ? activeBg : "transparent",
              color: active ? activeText : idleText,
              transition: "background-color 150ms ease, color 150ms ease",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
